import React, { useState } from 'react';
import { AssetUrls } from 'config/assets';
import { CardFinish, FINISH_LABELS } from 'utils/finishes';
import { getPromoTreatments, PromoTreatment } from 'utils/promoTreatments';
import './FinishTag.css';

/**
 * Icon sizes are fixed by the design (QA round 1, section 0): 9px inside a
 * tag, 11px in a table cell, 18px as a standalone mark. Never larger; these
 * are marks, not illustrations.
 */
export type FinishIconSize = 'tag' | 'cell' | 'mark';
const ICON_PX: Record<FinishIconSize, number> = { tag: 9, cell: 11, mark: 18 };

/**
 * Inline fallbacks for the three S3 swatches (graphite / rainbow / metallic
 * silver with a gold band), used only when the hosted asset fails to load so
 * the mark never renders as a broken image.
 */
const FALLBACK_SVG: Record<CardFinish, string> = {
  nonfoil:
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%235a5a63'/><stop offset='1' stop-color='%233d3d45'/></linearGradient></defs><rect x='2' y='2' width='44' height='44' rx='6' fill='url(%23g)'/><rect x='2.5' y='2.5' width='43' height='43' rx='5.5' fill='none' stroke='rgba(255,255,255,.3)'/></svg>",
  foil:
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'><defs><linearGradient id='g' x1='0' y1='1' x2='1' y2='0'><stop offset='0' stop-color='%23ff4081'/><stop offset='.2' stop-color='%23ffbf40'/><stop offset='.4' stop-color='%2369f0ae'/><stop offset='.6' stop-color='%2340c4ff'/><stop offset='.8' stop-color='%23b388ff'/><stop offset='1' stop-color='%23ff4081'/></linearGradient></defs><rect x='2' y='2' width='44' height='44' rx='6' fill='url(%23g)'/><rect x='2.5' y='2.5' width='43' height='43' rx='5.5' fill='none' stroke='rgba(255,255,255,.6)'/></svg>",
  etched:
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'><defs><linearGradient id='m' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%2343515d'/><stop offset='.3' stop-color='%238ea0b0'/><stop offset='.5' stop-color='%23d8c79a'/><stop offset='.68' stop-color='%2326303a'/><stop offset='1' stop-color='%23617387'/></linearGradient></defs><rect x='2' y='2' width='44' height='44' rx='6' fill='url(%23m)'/><rect x='2.5' y='2.5' width='43' height='43' rx='5.5' fill='none' stroke='%23f5e2be' stroke-opacity='.55'/></svg>",
};

interface FinishIconProps {
  finish: CardFinish;
  size?: FinishIconSize;
  className?: string;
  /** Decorative by default (the label or the cell carries the meaning) */
  title?: string;
  /** Swatch override: a promo treatment's swatch in place of the finish's own (addendum 2, 5.1) */
  src?: string;
}

/** The finish swatch on its own: 9px in tags, 11px in cells, 18px standalone. */
export const FinishIcon: React.FC<FinishIconProps> = ({ finish, size = 'tag', className = '', title, src }) => {
  const [failed, setFailed] = useState(false);
  const px = ICON_PX[size];
  return (
    <img
      src={failed ? FALLBACK_SVG[finish] : src ?? AssetUrls.icons.finish[finish]}
      width={px}
      height={px}
      alt={title ? FINISH_LABELS[finish] : ''}
      title={title}
      aria-hidden={title ? undefined : true}
      className={`finish-icon finish-icon--${size} finish-icon--${finish} ${className}`}
      data-finish={finish}
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
};

interface FinishTagProps {
  finish: CardFinish;
  /**
   * 'tag' (default) is the chip over a card image and inside dropdown rows;
   * 'cell' is the table-cell variant with the 11px icon.
   */
  size?: 'tag' | 'cell';
  className?: string;
  title?: string;
  /**
   * Scryfall promo_types of the printing (addendum 2, 5.1). When one is a
   * treatment, its swatch replaces the finish's own inside the chip and its
   * name follows the finish word as a muted "· Name" suffix. The word is
   * always the finish: a treatment is never a finish.
   */
  promoTypes?: unknown;
  /** A resolved treatment, when the caller already has one */
  treatment?: PromoTreatment | null;
}

/**
 * THE finish mark (QA round 1, section 1): one component for every surface.
 * Dark chip, the finish colour lives in the icon only, uppercase label. Never
 * re-tinted per surface. A promo treatment is never its own chip: it rides on
 * the finish tag as the swatch plus a muted suffix, never beside a price.
 */
export const FinishTag: React.FC<FinishTagProps> = ({ finish, size = 'tag', className = '', title, promoTypes, treatment }) => {
  const treatments = treatment ? [treatment] : getPromoTreatments(promoTypes);
  const lead = treatments[0] ?? null;
  const suffix = treatments.map((t) => t.short).join(', ');
  const fullTitle = title ?? (lead ? `${FINISH_LABELS[finish]} · ${treatments.map((t) => t.label).join(', ')}` : FINISH_LABELS[finish]);
  return (
    <span
      className={`finish-tag finish-tag--${size} finish-tag--${finish} ${lead ? 'finish-tag--treated' : ''} ${className}`}
      data-finish={finish}
      data-treatment={lead?.key}
      title={fullTitle}
    >
      <FinishIcon finish={finish} size={size} src={lead?.swatch} />
      <span className="finish-tag__label">{FINISH_LABELS[finish]}</span>
      {lead && <span className="finish-tag__treatment">· {suffix}</span>}
    </span>
  );
};

const MARK_GLYPH: Record<CardFinish, string> = { nonfoil: 'NF', foil: 'F', etched: 'E' };

interface FinishMarkProps {
  finish: CardFinish;
  className?: string;
  title?: string;
}

/**
 * The lettered mark for a TABLE CELL where no word label sits beside it
 * (QA round 1 addendum, 4.2): an 18px tile carrying NF / F / E over the
 * finish gradient. The glyph is not optional: colour alone fails for
 * colourblind users, disappears in grayscale, and the graphite non-foil tile
 * is nearly invisible against a #181818 row. Never a bare swatch here.
 */
export const FinishMark: React.FC<FinishMarkProps> = ({ finish, className = '', title }) => (
  <span
    className={`finish-mark finish-mark--${finish} ${className}`}
    data-finish={finish}
    title={title ?? FINISH_LABELS[finish]}
    aria-label={FINISH_LABELS[finish]}
    role="img"
  >
    {MARK_GLYPH[finish]}
  </span>
);

export default FinishTag;
